import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  TextField,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import { AssetDetailsAPI } from "../viewmodel/AssetDetailsAPI";

const MAX_LEN = 255;
const MAX_NOTES_LEN = 500;
export const RETURN_CONDITIONS = ["Good", "Damaged", "Missing parts"];

const ReturnModal = ({ open, onClose, onSuccess, cAssetData }) => {
  const [reason, setReason] = useState("");
  const [condition, setCondition] = useState("");
  const [notes, setNotes] = useState("");
  const [touched, setTouched] = useState(false);
  const [conditionTouched, setConditionTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const { handleToastOpen } = useToast();

  // Reset when the popup closes
  useEffect(() => {
    if (!open) {
      setReason("");
      setCondition("");
      setNotes("");
      setTouched(false);
      setConditionTouched(false);
      setLoading(false);
    }
  }, [open]);

  const isReasonValid = reason.trim().length > 0;
  const isConditionValid = RETURN_CONDITIONS.includes(condition);
  const isValid = isReasonValid && isConditionValid;

  const onReturn = async () => {
    if (!isValid) {
      setTouched(true);
      setConditionTouched(true);
      return;
    }
    try {
      setLoading(true);
      await AssetDetailsAPI.returnAsset(
        cAssetData.asset_id,
        reason.trim(),
        condition,
        notes.trim()
      );
      handleToastOpen("success", "Asset returned successfully");
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error returning asset:", error);
      handleToastOpen(
        "error",
        error.response?.data?.message || "Failed to return asset"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          backgroundColor: "#ffffff",
          boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.064)",
          padding: "20px",
          borderRadius: "5px",
          position: "relative",
          width: "400px",
          maxWidth: "90vw",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 8, right: 8, color: "#555" }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h6" sx={{ marginBottom: "1em" }}>
          Return Asset
        </Typography>

        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <b>Asset ID:</b> {cAssetData?.asset_id}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <b>Current employee:</b> {cAssetData?.employee_name}
          {cAssetData?.employee_code ? ` (${cAssetData.employee_code})` : ""}
        </Typography>

        <TextField
          label="Reason for return"
          placeholder="e.g. Employee left the organization"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onBlur={() => setTouched(true)}
          multiline
          rows={3}
          fullWidth
          required
          disabled={loading}
          inputProps={{ maxLength: MAX_LEN }}
          error={touched && !isReasonValid}
          helperText={
            touched && !isReasonValid ? "Please enter a reason to return the asset" : " "
          }
        />
        <Typography
          variant="caption"
          sx={{ display: "block", textAlign: "right", color: "#888", mt: -2.5, mb: 1 }}
        >
          {reason.length} / {MAX_LEN}
        </Typography>

        {/* Condition of the asset when it comes back */}
        <TextField
          select
          label="Condition"
          value={condition}
          onChange={(e) => setCondition(e.target.value)}
          onBlur={() => setConditionTouched(true)}
          fullWidth
          required
          size="small"
          disabled={loading}
          error={conditionTouched && !isConditionValid}
          helperText={
            conditionTouched && !isConditionValid
              ? "Please choose the condition of the asset"
              : " "
          }
          sx={{ mt: 1 }}
        >
          {RETURN_CONDITIONS.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Notes (optional)"
          placeholder="e.g. Screen cracked, charger missing"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={2}
          fullWidth
          disabled={loading}
          inputProps={{ maxLength: MAX_NOTES_LEN }}
        />
        <Typography
          variant="caption"
          sx={{ display: "block", textAlign: "right", color: "#888" }}
        >
          {notes.length} / {MAX_NOTES_LEN}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={onReturn}
            disabled={!isValid || loading}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Return"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ReturnModal;